/**
 * ActiveAdventureList Component
 * 
 * Displays a list of active adventure sessions for the current user.
 * Used in rich text rendering for <Active-Adventure-List> tags.
 * Items are clickable and will navigate to the existing session when clicked (not create new).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveAdventures } from '../../hooks/useActiveAdventures';
import { AdventureTitleImage } from './AdventureTitleImage';
import apiService from '../../services/api';
import useGameStore from '../../stores/gameStore';

export interface ActiveAdventureListProps {
  /**
   * Whether to exclude system adventures (e.g., 'storynity')
   */
  excludeSystemAdventures?: boolean;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Custom styles
   */
  style?: React.CSSProperties;
  
  /**
   * Optional custom button color for styling adventure buttons
   */
  customColor?: string;
}

/**
 * ActiveAdventureList component for displaying lists of active adventures in rich text
 */
export const ActiveAdventureList: React.FC<ActiveAdventureListProps> = ({
  excludeSystemAdventures = true,
  className = '',
  style,
  customColor
}) => {
  const navigate = useNavigate();
  const systemAdventureId = useGameStore((s) => s.systemAdventureId);
  const { adventures, loading, error } = useActiveAdventures();
  const [navigatingSessionId, setNavigatingSessionId] = React.useState<string | null>(null);
  const [titleImageIds, setTitleImageIds] = React.useState<Map<string, string>>(new Map());

  // Fetch title image IDs for all adventures
  React.useEffect(() => {
    if (adventures.length === 0) return;

    const fetchTitleImages = async () => {
      const imageMap = new Map<string, string>();
      
      await Promise.all(
        adventures.map(async (adventure) => {
          try {
            const header = await apiService.getAdventureHeader(adventure.session.adventureId);
            if (header.titleImageId != null) {
              imageMap.set(adventure.session.id, header.titleImageId);
            }
          } catch (err) {
            console.warn(`Failed to load adventure header for ${adventure.session.adventureId}:`, err);
          }
        })
      );

      setTitleImageIds(imageMap);
    };

    fetchTitleImages();
  }, [adventures]);

  const handleAdventureClick = (sessionId: string, playerId: string | undefined) => {
    if (!playerId || navigatingSessionId) return;
    
    setNavigatingSessionId(sessionId);
    // Navigate to existing session (not create new)
    navigate(`/scene/${sessionId}/${playerId}`);
  };

  // Filter out system adventures if requested
  const filteredAdventures = excludeSystemAdventures
    ? adventures.filter(adventure => adventure.session.adventureId !== systemAdventureId)
    : adventures;

  if (loading) {
    return (
      <div 
        className={`active-adventure-list loading ${className}`} 
        style={style}
        data-testid="active-adventure-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic">Loading active adventures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={`active-adventure-list error ${className}`} 
        style={style}
        data-testid="active-adventure-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400">Error loading active adventures: {error}</p>
        </div>
      </div>
    );
  }

  if (filteredAdventures.length === 0) {
    return (
      <div 
        className={`active-adventure-list empty ${className}`} 
        style={style}
        data-testid="active-adventure-list-empty"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic">No active adventures</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`active-adventure-list ${className}`} 
      style={style}
      data-testid="active-adventure-list"
    >
      <div
        className="active-adventure-list-items flex flex-wrap justify-center"
        style={{ gap: '1rem', rowGap: '0.5rem' }}
        data-testid="active-adventure-items-list"
      >
        {filteredAdventures.map((adventure) => {
          const isNavigating = navigatingSessionId === adventure.session.id;
          const titleImageId = titleImageIds.get(adventure.session.id);
          
          // Apply custom color styling if provided
          const getColorWithAlpha = (color: string, alpha: string) => {
            if (color.startsWith('#')) {
              return `${color}${alpha}`;
            }
            return color;
          };
          
          const buttonStyle: React.CSSProperties = { 
            width: '512px', 
            maxWidth: '100%', 
            marginBottom: '0.5rem' 
          };
          
          if (customColor && !isNavigating && adventure.playerId) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }
          
          return (
            <button
              key={adventure.session.id}
              onClick={() => handleAdventureClick(adventure.session.id, adventure.playerId)}
              disabled={isNavigating || !adventure.playerId}
              className="active-adventure-item-button adventure-item-button inventory-item-button"
              style={buttonStyle}
              data-testid={`active-adventure-item-${adventure.session.id}`}
              data-session-id={adventure.session.id}
              aria-label={`Resume ${adventure.session.adventureId}`}
            >
              <div className="active-adventure-item-image" style={{ width: '100%' }}>
                {isNavigating ? (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-gray-400 italic">Resuming...</span>
                  </div>
                ) : titleImageId ? (
                  <AdventureTitleImage
                    titleImageId={titleImageId}
                    variant="card"
                    alt={`Resume ${adventure.session.adventureId}`}
                    responsive={true}
                  />
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <span className="text-gray-400 italic">Loading image...</span>
                  </div>
                )}
              </div>
              {adventure.characterName && (
                <div className="text-center mt-2 text-sm text-gray-300">
                  {adventure.characterName}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveAdventureList;

